import './polyfills'
import { isServer, keepPreviousData, MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { basename } from './lib/utils'
import { withSdkParams } from './lib/sdk-params'
import { resolveRedirectTarget } from './lib/redirect-target'
import { persistQueryClient, removeOldestQuery } from '@tanstack/react-query-persist-client'
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import {
  createTRPCClient,
  httpBatchLink,
  httpBatchStreamLink,
  splitLink,
  TRPCClientError,
  type HTTPHeaders,
  type TRPCLink,
} from '@trpc/client'
import type { AppRouter } from 'server/trpc'
import type { inferRouterOutputs } from '@trpc/server'
import { trpcSSRCallFn } from './lib/trpc-server-fn'
import { shouldRetryQuery } from './lib/query-retry'
import { useAppStore } from './lib/app-store'
import { createTRPCOptionsProxy } from '@trpc/tanstack-react-query'
import { observable } from '@trpc/server/observable'
import { toast } from './lib/toast'
import { i18n, type MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import type { ErrorCode } from 'shared'
import { classifyTrpcErrorToast, pickIncompleteTestPath } from './main-helpers'

/**
 * Shared client singletons (query client, tRPC client/proxy, persister).
 *
 * Historically this file also bootstrapped the React tree (createRoot). Under
 * TanStack Start the app is mounted from `src/router.tsx` (`getRouter`) + the
 * Start client entry instead — this module is now just the singletons that the
 * rest of the app imports via `@/main` (`trpc`, `queryClient`, ...).
 *
 * Everything here is written to be import-safe on the server (the SPA shell is
 * rendered server-side): no top-level `window`/`localStorage` access.
 */

// Factory so the server can mint a fresh client per request (a module singleton would
// leak one request's dehydrated cache into the next on the long-lived Bun server). The
// client uses the exported singleton below (one per browser, HMR-stable + persisted).
export function makeQueryClient(): QueryClient {
  return new QueryClient({
    // Error side effects (auth/context redirects + toasts) live on the caches, NOT a tRPC link, so they
    // can be OBSERVER-GATED. A router hover-preload (defaultPreload: 'intent') prefetches the target
    // route's loader queries; if one errors it has no observer (the user hasn't navigated), so a toast or
    // redirect would fire "from nowhere". Gating the query path on `getObserversCount() > 0` keeps preload
    // errors silent (and harmless — prefetchQuery swallows them) while a query a mounted component is
    // actually showing still surfaces. Mutations have no render boundary, so they always surface.
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (query.getObserversCount() === 0) return
        handleClientTrpcError(error)
      },
    }),
    mutationCache: new MutationCache({
      onError: (error) => handleClientTrpcError(error),
    }),
    defaultOptions: {
      queries: {
        // Client-only transient-failure retry (see lib/query-retry): on a lossy link a single dropped
        // request would otherwise fail a query permanently, since the default here was `retry: false`.
        // SSR keeps no-retry so a render never blocks on backoff (the in-process ssrLink has no network to
        // retry against). Mutations are untouched — a retried mutation could double-submit.
        retry: isServer ? false : shouldRetryQuery,
        // Exponential backoff, capped: 0.5s → 1s → 2s, so a genuinely-down backend still surfaces its
        // error within a few seconds instead of hanging the UI.
        retryDelay: (attemptIndex) => Math.min(500 * 2 ** attemptIndex, 4000),
        staleTime: 60 * 1000,
        // Bounded (not Infinity): report/team queries are keyed on the selected member subset,
        // so browsing through members spawns a distinct cache entry per combination. With
        // gcTime: Infinity those never got collected and accumulated for the whole session,
        // progressively slowing the app until a re-login reset the cache. 5 min reclaims the
        // stale combinations while keeping recently-viewed data warm.
        gcTime: 5 * 60 * 1000,
        placeholderData: keepPreviousData,
      },
    },
  })
}

export const queryClient = makeQueryClient()

// Picks the first unfinished questionnaire so a TEST_INCOMPLETE redirect resumes where the user
// left off (competences → values → personality), reading the cached user. Falls back to the first
// test when the user isn't cached yet.
export function firstIncompleteTestPath(): string {
  const user = queryClient.getQueryData<{
    testPassed?: boolean | null
    testPassedValues?: boolean | null
    testPassedPersonality?: boolean | null
  } | null>(trpc.auth.getUser.queryKey())
  return pickIncompleteTestPath(user)
}

// Backend control-flow errors arrive as a stable machine code in the tRPC error `message` (see
// `ErrorCode` in shared). The server never localizes them — we render the matching Lingui
// translation here so a single source of truth (the .po catalogs) owns the wording. Errors without a
// known code fall through to their raw message. Keep this map in sync with `ErrorCode`.
const ERROR_TRANSLATIONS: Partial<Record<ErrorCode, MessageDescriptor>> = {
  INVALID_CREDENTIALS: msg`Invalid email or password`,
  NOT_YOUR_EMPLOYER: msg`This is not one of your employers`,
  INSUFFICIENT_PERMISSIONS: msg`You don't have permission to perform this action`,
  INTERFACE_SWITCH_UNAVAILABLE: msg`That view isn't available`,
  SUPER_ADMIN_ONLY: msg`This area is restricted to D'Anchiano super-admins`,
  OFFER_NOT_FOUND: msg`Offer not found`,
  APPLICANT_NOT_FOUND: msg`Applicant not found`,
  APPLICANT_ONLY: msg`This action is only available to professional accounts`,
  COMPANY_ONLY: msg`This action is only available to company accounts`,
  EMAIL_IN_USE: msg`This email has already been used`,
  EMAIL_REQUIRED: msg`An email address is required`,
  SOCIAL_ID_IN_USE: msg`This social account is already linked to another account`,
  TOKEN_INVALID: msg`This link is invalid or has expired`,
  ORG_NOT_FOUND: msg`Organization not found`,
  TEAM_NOT_FOUND: msg`Team not found`,
  TEAM_ALREADY_EXISTS: msg`A team with this name already exists`,
  MEMBER_ALREADY_ON_TEAM: msg`This member is already on the team`,
  MEMBER_NOT_ON_TEAM: msg`This member is not on the team`,
  COMPANY_NOT_FOUND: msg`Company not found`,
  CUSTOMER_NOT_FOUND: msg`Customer not found`,
  INVALID_PRODUCT: msg`Invalid or unavailable product`,
  PAYMENT_UNAVAILABLE: msg`Payments are temporarily unavailable. Please try again.`,
  APP_NOT_FOUND: msg`Application not found`,
  PROFILE_BLOCKED: msg`This profile isn't available`,
  PROFILE_NOT_DEFINED: msg`No profile selected`,
  TEST_NOT_PASSED: msg`The psychographic test isn't completed yet`,
  INVITATION_INVALID: msg`This invitation is invalid or has expired`,
  TEAMS_REEVALUATE_FORBIDDEN: msg`You don't have permission to re-evaluate this member`,
  DOMAIN_MISMATCH: msg`This request isn't allowed from this domain`,
}

// Side effects for a client-side tRPC error: an auth/context redirect, or a toast. Decided purely from
// the error's machine `code` + transport `dataCode` (see classifyTrpcErrorToast — pure + unit-tested).
// Wired into the QueryClient's query/mutation caches (makeQueryClient) rather than a tRPC link, so the
// query path can be observer-gated (no toast on a background preload). Runs ONLY in the browser — SSR
// query errors are handled by the router error boundary (route-error), and `window` is undefined there.
function handleClientTrpcError(error: unknown): void {
  if (typeof window === 'undefined') return
  const err = error as { message?: string; data?: { code?: string } }
  const code = (err.message ?? '') as ErrorCode
  const descriptor = ERROR_TRANSLATIONS[code]
  const action = classifyTrpcErrorToast({ code, dataCode: err.data?.code, hasTranslation: descriptor != null })
  if (action === 'redirect-login') {
    // A 401 with no live session → send to login (INVALID_CREDENTIALS is excluded so a failed login keeps
    // its in-place message instead of reloading the form). resolveRedirectTarget unwraps the current URL so
    // firing this while already on /login reuses the real destination instead of nesting /login into itself.
    const target = resolveRedirectTarget(window.location.pathname + window.location.search)
    const query = target ? `?redirect=${encodeURIComponent(target)}` : ''
    window.location.href = withSdkParams(`${basename}/login${query}`)
    return
  }
  if (action === 'redirect-test') {
    // Resume the questionnaire (carrying API/SDK token-link params). Guard against a loop while already
    // on a /test route (those pages only call test/auth endpoints, which aren't gated).
    if (!window.location.pathname.includes('/test')) {
      window.location.href = withSdkParams(`${basename}${firstIncompleteTestPath()}`)
    }
    return
  }
  if (action === 'redirect-report') {
    // A self-registered professional reached a company/employee-only section → their own profile.
    if (!window.location.pathname.includes('/report')) {
      window.location.href = withSdkParams(`${basename}/report`)
    }
    return
  }
  if (action === 'redirect-company') {
    // The inverse: an employee in company view hit their own /report page → the company home.
    if (!window.location.pathname.includes('/jobs')) {
      window.location.href = withSdkParams(`${basename}/jobs`)
    }
    return
  }
  if (action === 'toast-generic') {
    // A 500's detail (raw SQL / stack) is redacted by the server, so show a generic toast.
    toast.error(i18n._(msg`Something went wrong. Please try again.`))
    return
  }
  // toast-translated → render the known code's translation; toast-raw → surface the raw message.
  toast.error(action === 'toast-translated' && descriptor ? i18n._(descriptor) : (err.message ?? ''))
}

// Terminating link for SSR (data-only loaders): dispatches through `trpcSSRCallFn`,
// a server function that runs the procedure in-process against `appRouter`. The
// server function boundary tree-shakes the `server/*` imports out of the client
// bundle, so this link only ever runs on the server (see `isServer` switch below).
const ssrLink: TRPCLink<AppRouter> = () => {
  return ({ op }) => {
    return observable((observer) => {
      const type = op.type === 'subscription' ? 'query' : op.type
      trpcSSRCallFn({ data: { path: op.path, input: op.input, type } })
        .then((result) => {
          observer.next({ result: { type: 'data', data: result } })
          observer.complete()
        })
        .catch((err: unknown) => {
          observer.error(
            err instanceof TRPCClientError
              ? err
              : new TRPCClientError<AppRouter>(err instanceof Error ? err.message : String(err), {
                  cause: err instanceof Error ? err : undefined,
                }),
          )
        })
    })
  }
}

// Shared HTTP transport options for the client-side links (the streaming link for queries and the
// buffered link for mutations). Keeping ONE options object means both links hit the same `/trpc`
// endpoint and send the same auth/locale headers — only their batching/streaming behavior differs.
const httpLinkOptions = {
  url: `${basename}/trpc`,
  headers() {
    const headers: HTTPHeaders = {}

    const { clientId: storeClientId, locale, userToken: storeUserToken } = useAppStore.getState().store
    const clientId = new URLSearchParams(location.search).get('clientId') ?? storeClientId
    // Fall back to the store (captured from the URL at load) like clientId does — so the
    // token survives a navigation that momentarily drops it from the URL.
    const userToken = new URLSearchParams(location.search).get('token') ?? storeUserToken

    if (clientId) headers['Client-id'] = clientId
    if (userToken) headers['Authorization'] = `Bearer ${userToken}`
    if (locale) headers['Accept-language'] = locale

    if (typeof window !== 'undefined') {
      if (window.document.referrer) {
        const pathArray = window.document.referrer.split('/')
        const origin = `${pathArray[0]}//${pathArray[2]}`
        headers['Iframe-origin'] = origin
      } else {
        const pathArray = window.location.href.split('/')
        const origin = `${pathArray[0]}//${pathArray[2]}`
        headers['Iframe-origin'] = origin
      }
    }

    return headers
  },
}

// Exported so a few call sites can fetch a query imperatively WITHOUT publishing into the react-query
// cache (e.g. company registration fetches the fresh user, then writes it to the getUser cache inside a
// view transition so the auth-card → wizard box morphs deterministically — see companies/register-form).
export const trpcClient = createTRPCClient<AppRouter>({
  // On the server the only consumers are `/_app` route loaders (data-only SSR); the
  // window-coupled `headers()` below would crash there, so the server uses the
  // in-process `ssrLink` exclusively. The client keeps the existing HTTP transport.
  links: isServer
    ? [ssrLink]
    : [
        // Error side effects (toast / redirect) are handled by the query & mutation caches
        // (makeQueryClient), not a link, so they can be observer-gated — see handleClientTrpcError.
        // Mutations set the auth session cookie (login / register / logout / social link). The
        // streaming link keeps the response connection open for batching, so the post-login client
        // navigation aborts it (net::ERR_ABORTED) before the browser commits the response's
        // Set-Cookie — the session was silently dropped. Route MUTATIONS through the non-streaming
        // httpBatchLink so the full response (Set-Cookie included) lands before we navigate; QUERIES
        // keep httpBatchStreamLink so streamed SSR / data loading is unchanged. Both share the same
        // url + headers, so batching and auth headers behave identically.
        splitLink({
          condition: (op) => op.type === 'mutation',
          true: httpBatchLink(httpLinkOptions),
          false: httpBatchStreamLink(httpLinkOptions),
        }),
      ],
})

export const trpc: ReturnType<typeof createTRPCOptionsProxy<AppRouter>> = createTRPCOptionsProxy<AppRouter>({
  client: trpcClient,
  queryClient,
})

export type User = NonNullable<inferRouterOutputs<AppRouter>['auth']['getUser']>

// On the server the persister has no storage and is inert; on the client it
// hydrates/persists the query cache to localStorage.
export const persister = createAsyncStoragePersister({
  storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  retry: removeOldestQuery,
})

// Bump when a query's cached shape changes incompatibly (schema/return-type change) or when a
// release must start from a clean persisted cache. A mismatch discards the persisted blob instead of
// rehydrating stale/incompatible data into the running app.
export const PERSIST_BUSTER = 'v2'

// What gets written to (and restored from) localStorage. Shared by the eager `persistQueryClient`
// below and the `PersistQueryClientProvider` in router.tsx so both agree on the policy.
export const persistOptions = {
  persister,
  buster: PERSIST_BUSTER,
}

export const clearQueryClient = () => {
  queryClient.clear()
  // It takes one second to sync the changes with the storage, so we just remove the item directly
  if (typeof window !== 'undefined') window.localStorage.removeItem('REACT_QUERY_OFFLINE_CACHE')
}

// localStorage tag recording WHICH account the persisted query cache belongs to. The persisted cache
// survives client-side login navigations, and the social-OAuth login path lands via a full document
// load that REHYDRATES the persisted blob instead of clearing it (only password login / logout call
// clearQueryClient). The identity guard in __root.tsx compares this tag against the resolved getUser
// id and purges the cache on a mismatch, so a previous account's identity-scoped queries (e.g.
// getMyEmployeeProfile) can't leak into the next user's session. Kept OUT of REACT_QUERY_OFFLINE_CACHE
// so it survives clearQueryClient() (which removes that key).
export const PERSISTED_IDENTITY_KEY = 'dc-persisted-user-id'

// TODO: We're calling it also here instead of relying only on <PersistQueryClientProvider />
// because on quick reload is still not restoring from persister
if (typeof window !== 'undefined') {
  persistQueryClient({ queryClient, ...persistOptions })
}
